import { appConfig } from "@/config/app.config";
import type {
  MediaLinkValidationResult,
  MediaValidationResult,
} from "@/types/transcript";

const supportedExtensions = [
  ".mp4",
  ".mov",
  ".webm",
  ".mp3",
  ".m4a",
  ".wav",
  ".ogg",
  ".mpeg",
  ".mpga",
  ".m4v",
];
const supportedMimePrefixes = ["audio/", "video/"];

function hostnameMatches(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

export function isSupportedSocialMediaUrl(url: URL): boolean {
  return Boolean(socialMediaProviderFromUrl(url));
}

export function socialMediaProviderFromUrl(url: URL): "TikTok" | "Instagram" | null {
  const hostname = url.hostname.toLowerCase();
  const pathname = url.pathname.toLowerCase();

  if (hostnameMatches(hostname, "tiktok.com")) {
    return pathname.length > 1 ? "TikTok" : null;
  }

  if (hostnameMatches(hostname, "instagram.com")) {
    return /^\/(reel|reels|p|tv)\//.test(pathname) ? "Instagram" : null;
  }

  return null;
}

export function detectPremiumSocialMediaLink(
  input: string,
): "TikTok" | "Instagram" | null {
  try {
    return socialMediaProviderFromUrl(new URL(input.trim()));
  } catch {
    return null;
  }
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  const mb = bytes / 1024 / 1024;
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

export function validateMediaFile(
  file?: File | Blob | null,
): MediaValidationResult {
  if (!file) {
    return {
      ok: false,
      error: "Choose a video or audio file before generating a transcript.",
    };
  }

  if (!file.size) {
    return { ok: false, error: "The selected media file is empty." };
  }

  const maxBytes = appConfig.limits.maxUploadMb * 1024 * 1024;
  if (file.size > maxBytes) {
    return {
      ok: false,
      error: `Free uploads are limited to ${appConfig.limits.maxUploadMb} MB.`,
    };
  }

  const type = file.type.toLowerCase();
  const name = "name" in file ? String(file.name).toLowerCase() : "";
  const hasMediaMime = supportedMimePrefixes.some((prefix) =>
    type.startsWith(prefix),
  );
  const hasMediaExtension = supportedExtensions.some((extension) =>
    name.endsWith(extension),
  );

  if (!hasMediaMime && !hasMediaExtension) {
    return {
      ok: false,
      error: "Use an MP4, MOV, WebM, MP3, M4A, WAV, or OGG file.",
    };
  }

  return { ok: true };
}

export function validateDirectMediaUrl(
  input: string,
): MediaLinkValidationResult {
  const value = input.trim();
  if (!value) {
    return {
      ok: false,
      error:
        "Paste a direct video or audio URL before generating a transcript.",
    };
  }

  if (value.length > appConfig.limits.maxMediaUrlLength) {
    return {
      ok: false,
      error: "That media URL is too long. Use a direct link to a media file.",
    };
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return {
      ok: false,
      error:
        "Paste a valid direct media URL that starts with http:// or https://.",
    };
  }

  if (!["http:", "https:"].includes(url.protocol)) {
    return {
      ok: false,
      error: "Only http:// and https:// media URLs are supported.",
    };
  }

  if (
    ["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(
      url.hostname.toLowerCase(),
    )
  ) {
    return { ok: false, error: "Local media URLs are not supported." };
  }

  const pathname = url.pathname.toLowerCase();
  const hasMediaExtension = supportedExtensions.some((extension) =>
    pathname.endsWith(extension),
  );
  const socialProvider = socialMediaProviderFromUrl(url);
  if (socialProvider) {
    return {
      ok: false,
      error: `${socialProvider} link transcription is coming as a premium feature. Join the waitlist for early access, or use upload, record, or a direct media file URL today.`,
    };
  }

  if (!hasMediaExtension) {
    return {
      ok: false,
      error:
        "Use a direct MP4, MOV, WebM, MP3, M4A, WAV, or OGG file URL.",
    };
  }

  return { ok: true, mediaUrl: url.toString() };
}

export function filenameFromBlob(file: File | Blob, fallback: string): string {
  return "name" in file && file.name ? file.name : fallback;
}

/**
 * Encodes an AudioBuffer as a 16-bit PCM WAV file.
 * Processing is split into chunks with event-loop yields between each chunk
 * so the UI thread stays responsive (spinner keeps spinning, no frozen cursor).
 */
async function encodeWav(audioBuffer: AudioBuffer): Promise<ArrayBuffer> {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const numSamples = audioBuffer.length;
  const bytesPerSample = 2;
  const dataSize = numSamples * numChannels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++)
      view.setUint8(offset + i, str.charCodeAt(i));
  };

  // WAV header (44 bytes — synchronous but negligible)
  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  // Pre-fetch channel data arrays once to avoid per-sample getChannelData calls
  const channels: Float32Array[] = [];
  for (let ch = 0; ch < numChannels; ch++) {
    channels.push(audioBuffer.getChannelData(ch));
  }

  // Process samples in chunks, yielding to the event loop between each chunk
  // so CSS animations (spinner) and React state updates can render normally.
  const CHUNK_SIZE = 32_768; // ~2 s at 16 kHz per chunk
  let offset = 44;
  for (let i = 0; i < numSamples; i += CHUNK_SIZE) {
    const end = Math.min(i + CHUNK_SIZE, numSamples);
    for (let s = i; s < end; s++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][s]));
        view.setInt16(offset, (sample * 32767) | 0, true);
        offset += 2;
      }
    }
    // Yield so the browser can paint a frame before the next chunk
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  return buffer;
}

/**
 * Extracts the audio track from a video file and returns it as a 16kHz mono WAV.
 * Workers AI Whisper cannot decode video containers; this converts them to a
 * format the model can process.
 */
export async function extractAudioFromVideo(
  file: File | Blob,
  fallbackFilename = "video-transcript-media",
): Promise<File> {
  console.log(
    `[audio-extract] Reading file buffer (${(file.size / 1024 / 1024).toFixed(1)} MB)…`,
  );
  const arrayBuffer = await file.arrayBuffer();

  console.log("[audio-extract] Decoding audio data…");
  const audioCtx = new AudioContext();
  let decoded: AudioBuffer;
  try {
    decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    await audioCtx.close();
  }
  console.log(
    `[audio-extract] Decoded — ${decoded.duration.toFixed(1)}s, ${decoded.sampleRate} Hz, ${decoded.numberOfChannels}ch`,
  );

  console.log("[audio-extract] Resampling to 16 kHz mono…");
  const targetRate = 16000;
  const offlineCtx = new OfflineAudioContext(
    1,
    Math.ceil(decoded.duration * targetRate),
    targetRate,
  );
  const source = offlineCtx.createBufferSource();
  source.buffer = decoded;
  source.connect(offlineCtx.destination);
  source.start(0);

  const rendered = await offlineCtx.startRendering();
  console.log(
    `[audio-extract] Resampled — ${rendered.length} samples. Encoding WAV…`,
  );

  const wav = await encodeWav(rendered);
  const wavMb = (wav.byteLength / 1024 / 1024).toFixed(1);
  console.log(`[audio-extract] WAV encoded (${wavMb} MB). Uploading…`);

  const sourceName =
    "name" in file && file.name ? file.name : fallbackFilename;
  const baseName = sourceName.replace(/\.[^.]+$/, "");
  return new File([wav], `${baseName}.wav`, { type: "audio/wav" });
}
